import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate, formatFCFA } from "@/lib/format";
import { Loader } from "@/components/common";
import { PageTitle, Panel } from "./AdminShell";
import { ORDER_STATUS, statusMeta } from "@/lib/labels";
import { ChevronDown, Mail, Phone, User } from "lucide-react";

const PROVIDER = {
  email: { label: "Email", color: "bg-blue-100 text-blue-700" },
  google: { label: "Google", color: "bg-red-100 text-red-700" },
  phone: { label: "Téléphone", color: "bg-teal-100 text-teal-700" },
  admin: { label: "Créé par admin", color: "bg-slate-200 text-slate-600" },
};

export default function AdminClients() {
  const [clients, setClients] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/admin/clients").then((r) => setClients(r.data)); }, []);

  const expand = async (c) => {
    if (open === c.user_id) { setOpen(null); return; }
    setOpen(c.user_id); setDetail(null);
    try {
      const { data } = await api.get(`/admin/clients/${c.user_id}`);
      setDetail(data);
    } catch {
      setDetail({ error: true });
    }
  };

  if (!clients) return <Loader />;
  return (
    <div>
      <PageTitle>Clients</PageTitle>
      <div className="space-y-2">
        {clients.map((c) => {
          const p = PROVIDER[c.auth_provider] || PROVIDER.email;
          return (
            <Panel key={c.user_id} data-testid={`client-${c.user_id}`}>
              <button onClick={() => expand(c)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`client-toggle-${c.user_id}`}>
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  {c.picture ? <img src={c.picture} alt="" className="w-full h-full rounded-full object-cover" /> : <User className="w-4 h-4 text-slate-400" />}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className="text-xs text-slate-400 truncate">{c.email}</p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.color}`} data-testid={`client-provider-${c.user_id}`}>{p.label}</span>
                <span className="ml-auto text-sm font-num text-slate-600 shrink-0">{c.orders_count} cmd</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open === c.user_id ? "rotate-180" : ""}`} />
              </button>

              {open === c.user_id && (
                <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50" data-testid={`client-detail-${c.user_id}`}>
                  {!detail ? <Loader /> : detail.error ? <p className="text-sm text-red-500">Impossible de charger la fiche.</p> : (
                    <>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-600 mb-3">
                        <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {detail.client.email}</span>
                        <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {detail.client.phone || "—"}</span>
                        <span>Inscrit le {formatDate(detail.client.created_at)}</span>
                      </div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Commandes ({detail.orders.length})</p>
                      {detail.orders.length === 0 ? <p className="text-sm text-slate-400">Aucune commande.</p> : (
                        <div className="space-y-1.5">
                          {detail.orders.map((o) => {
                            const m = statusMeta(ORDER_STATUS, o.status);
                            return (
                              <div key={o.id} className="flex items-center gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-slate-100" data-testid={`client-order-${o.id}`}>
                                <span className="font-num font-semibold">{o.order_number}</span>
                                <span className="text-xs text-slate-400">{formatDate(o.created_at)} · {o.items_count} article(s)</span>
                                <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${m.color}`}>{m.label}</span>
                                <span className="font-num font-bold text-[#C85A32] w-24 text-right">{formatFCFA(o.total)}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </Panel>
          );
        })}
      </div>
    </div>
  );
}
